import type {
  UnifiedState,
  CommandResult,
  CombatAction,
  CombatActionResult,
  ServerCombatState,
  CrawlData,
  CrawlRunState,
  CrawlDetails,
  CampaignData,
  UnitDropData,
  ItemData,
  SoulloomResult,
  CraftResult,
  CurrencyDrop,
  ShopListingData,
  InspectData,
  PatternStashItem,
  ActiveEventBundle,
  NotificationData,
} from '../types';
import { setTelemetryAuth } from './Telemetry';

class APIManagerClass {
  private accessToken: string | null = null;

  setAccessToken(token: string): void {
    this.accessToken = token;
    setTelemetryAuth(token);
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<CommandResult<T>> {
    try {
      const response = await fetch(path, {
        method,
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Request failed' };
      }

      return { success: true, data: data.data };
    } catch (error) {
      console.error(`[APIManager] ${method} ${path} failed:`, error);
      return { success: false, error: 'Network error' };
    }
  }

  // Auth
  async exchangeToken(code: string): Promise<CommandResult<{ access_token: string }>> {
    return this.request('POST', '/api/auth/token', { code });
  }

  // Unified state - called once on boot; also called by panels to refresh after
  // mutating actions. We side-effect StateManager.achievementData on every call
  // so the AchievementsManager listener sees newly[] unlocks even when the
  // calling panel only batchUpdates a subset of state.
  async fetchUnifiedState(): Promise<CommandResult<UnifiedState>> {
    const result = await this.request<UnifiedState>('GET', '/api/state');
    if (result.success && result.data?.achievementData) {
      // Lazy import to avoid a require cycle (StateManager → ... → APIManager → ...).
      void import('./StateManager').then(({ StateManager }) => {
        StateManager.set('achievementData', result.data!.achievementData);
      });
    }
    return result;
  }

  // Player actions
  async createPlayer(): Promise<CommandResult<{ player: unknown; created: boolean }>> {
    return this.request('POST', '/api/player');
  }

  // Combat
  async submitAction(action: CombatAction): Promise<CommandResult<CombatActionResult>> {
    return this.request('POST', '/api/combat/action', action);
  }

  async getCombatState(): Promise<CommandResult<ServerCombatState | null>> {
    return this.request('GET', '/api/combat/state');
  }

  async abandonCombat(): Promise<CommandResult<null>> {
    return this.request('POST', '/api/combat/abandon');
  }

  async debugKillAll(): Promise<CommandResult<CombatActionResult>> {
    return this.request('POST', '/api/combat/debug-kill');
  }

  // Crawl
  async startCrawl(unitIds?: string[]): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', '/api/crawl/start', unitIds ? { unitIds } : undefined);
  }

  async advanceCrawl(): Promise<CommandResult<{ run: CrawlRunState; combatResult?: CombatActionResult; unitDrop?: UnitDropData; itemDrop?: ItemData; extraItemDrops?: ItemData[]; currencyDrops?: CurrencyDrop[]; unitLevelUps?: { unitId: string; unitName: string; oldLevel: number; newLevel: number }[]; emberDropped?: boolean }>> {
    return this.request('POST', '/api/crawl/advance');
  }

  async getTodaysCrawl(): Promise<CommandResult<CrawlData>> {
    return this.request('GET', '/api/crawl/today');
  }

  async resetDailyCrawl(): Promise<CommandResult<{ reset: boolean }>> {
    return this.request('POST', '/api/crawl/reset');
  }

  async abandonCrawl(): Promise<CommandResult<{ abandoned: boolean }>> {
    return this.request('POST', '/api/crawl/abandon');
  }

  // Event Crawl
  async getEventCrawl(): Promise<CommandResult<CrawlData>> {
    return this.request('GET', '/api/crawl/event');
  }

  async startEventCrawl(unitIds?: string[]): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', '/api/crawl/event/start', unitIds ? { unitIds } : undefined);
  }

  async resetEventCrawl(): Promise<CommandResult<{ reset: boolean }>> {
    return this.request('POST', '/api/crawl/event/reset');
  }

  // Crawl details (data-driven summary)
  async getCrawlDetails(crawlId: string): Promise<CommandResult<CrawlDetails>> {
    return this.request('GET', `/api/crawl/${crawlId}/details`);
  }

  // Free Crawl
  async startFreeCrawl(unitIds?: string[]): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', '/api/crawl/free/start', unitIds ? { unitIds } : undefined);
  }

  async resetFreeCrawl(): Promise<CommandResult<{ reset: boolean }>> {
    return this.request('POST', '/api/crawl/free/reset');
  }

  async chooseFork(pathId: string): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', '/api/crawl/choose-fork', { pathId });
  }

  // Campaign
  async getCampaign(): Promise<CommandResult<CampaignData>> {
    return this.request('GET', '/api/campaign');
  }

  async startCampaignRun(unitIds?: string[]): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', '/api/campaign/start', unitIds ? { unitIds } : undefined);
  }

  async startCampaignReplay(crawlId: string, unitIds?: string[]): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', '/api/campaign/replay', { crawlId, ...(unitIds ? { unitIds } : {}) });
  }

  async resolveCampaignDecision(crawlId: string, choice: 'kill' | 'spare'): Promise<CommandResult<{ choice: string; templateId: string; unitId: string }>> {
    return this.request('POST', '/api/campaign/decision', { crawlId, choice });
  }

  // Units / Masteries
  async investMasteryRank(unitId: string, masteryId: string): Promise<CommandResult<{ masteryRanks: Record<string, number>; unspentMasteryPoints: number }>> {
    return this.request('POST', `/api/units/${unitId}/masteries/invest`, { masteryId });
  }

  async investMasteryBatch(unitId: string, investments: { masteryId: string; ranks: number }[]): Promise<CommandResult<{ masteryRanks: Record<string, number>; unspentMasteryPoints: number }>> {
    return this.request('POST', `/api/units/${unitId}/masteries/invest-batch`, { investments });
  }

  async setUnitSkin(unitId: string, skinId: string | null): Promise<CommandResult<{ activeSkinId: string | null }>> {
    return this.request('POST', `/api/units/${unitId}/skin`, { skinId });
  }

  async spendMemory(unitId: string, tier: string, count: number): Promise<CommandResult<{ levelUps: { unitId: string; unitName: string; oldLevel: number; newLevel: number; masteryPointsGained: number }[]; xpGranted: number }>> {
    return this.request('POST', `/api/units/${unitId}/spend-memory`, { tier, count });
  }

  async equipParty(unitIds: string[]): Promise<CommandResult<{ equipped: boolean }>> {
    return this.request('POST', '/api/units/equip', { unitIds });
  }

  // Items
  async equipItem(unitId: string, itemId: string): Promise<CommandResult<{ equipped: boolean }>> {
    return this.request('POST', '/api/items/equip', { unitId, itemId });
  }

  async unequipItem(unitId: string, slot: 'weapon' | 'armor' | 'accessory' | 'offhand'): Promise<CommandResult<{ unequipped: boolean }>> {
    return this.request('POST', '/api/items/unequip', { unitId, slot });
  }

  // Skill equip/unequip
  async equipSkill(unitId: string, abilityId: string, slot: number): Promise<CommandResult<{ equipped: boolean }>> {
    return this.request('POST', '/api/skills/equip', { unitId, abilityId, slot });
  }

  async unequipSkill(unitId: string, slot: number): Promise<CommandResult<{ unequipped: boolean }>> {
    return this.request('POST', '/api/skills/unequip', { unitId, slot });
  }

  // Item salvage
  async deleteItem(itemId: string): Promise<CommandResult<{ salvaged: boolean; currencyGained: Record<string, number> }>> {
    return this.request('DELETE', `/api/items/${itemId}`);
  }

  // Inventory
  async moveInventorySlot(type: 'item', id: string, toSlot: number): Promise<CommandResult<{ moved: boolean }>> {
    return this.request('POST', '/api/inventory/move', { type, id, toSlot });
  }

  // Soulloom
  async soulloomRecombine(unitIdA: string, unitIdB: string): Promise<CommandResult<SoulloomResult>> {
    return this.request('POST', '/api/soulloom/recombine', { unitIdA, unitIdB });
  }

  // Crafting
  async craftReroll(targetType: string, targetId: string): Promise<CommandResult<CraftResult>> {
    return this.request('POST', '/api/craft/reroll', { targetType, targetId });
  }

  async craftAddAffix(targetType: string, targetId: string): Promise<CommandResult<CraftResult>> {
    return this.request('POST', '/api/craft/add-affix', { targetType, targetId });
  }

  async craftRemoveAffix(targetType: string, targetId: string): Promise<CommandResult<CraftResult>> {
    return this.request('POST', '/api/craft/remove-affix', { targetType, targetId });
  }

  async craftUpgradeRarity(targetType: string, targetId: string): Promise<CommandResult<CraftResult>> {
    return this.request('POST', '/api/craft/upgrade-rarity', { targetType, targetId });
  }

  async craftApplyDye(itemId: string, color: string, tier: string): Promise<CommandResult<CraftResult>> {
    return this.request('POST', '/api/craft/apply-dye', { itemId, color, tier });
  }

  // Shop
  async getShop(): Promise<CommandResult<{ listings: ShopListingData[]; activeEvent?: ActiveEventBundle }>> {
    return this.request('GET', '/api/shop');
  }

  async buyShopListing(listingId: string): Promise<CommandResult<{ listing: ShopListingData; entity: unknown; playerGold: number }>> {
    return this.request('POST', '/api/shop/buy', { listingId });
  }

  // Admin / debug
  async adminGrantItem(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-item');
  }

  async adminGrantUnit(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-unit');
  }

  async adminGrantAllStrands(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-all-strands');
  }

  async adminGrantCurrencies(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-currencies');
  }

  async adminGrantMemories(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-memories');
  }

  async adminLevelUpUnit(unitId: string): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/levelup-unit', { unitId });
  }

  async adminGrantAllDyes(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-all-dyes');
  }

  async adminRefreshShop(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/refresh-shop');
  }

  async adminGrantSkins(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-skins');
  }

  async adminGrantUnique(baseId: string = 'vothalas_carapace'): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-unique', { baseId });
  }

  async adminRunPattern(typeId: string = 'mine_caverns'): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/run-pattern', { typeId });
  }

  async adminGrantPattern(typeId: string = 'mine_caverns', rarity: string = 'rare'): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/grant-pattern', { typeId, rarity });
  }

  async getPatterns(): Promise<CommandResult<{ patterns: PatternStashItem[] }>> {
    return this.request('GET', '/api/patterns');
  }

  async runPattern(patternId: string, unitIds?: string[]): Promise<CommandResult<{ run: CrawlRunState; combatResult: CombatActionResult }>> {
    return this.request('POST', `/api/patterns/${patternId}/run`, { unitIds });
  }

  async deletePattern(patternId: string): Promise<CommandResult<{ consumed: boolean }>> {
    return this.request('DELETE', `/api/patterns/${patternId}`);
  }

  async upgradePattern(patternId: string): Promise<CommandResult<{ pattern: PatternStashItem }>> {
    return this.request('POST', `/api/patterns/${patternId}/upgrade`);
  }

  async adminSkipCampaignStep(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/clear-campaign-step');
  }

  async adminCompleteCampaignStep(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/complete-campaign-step');
  }

  async adminResetCampaign(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/reset-campaign');
  }

  async adminResetAccount(): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/admin/reset-account');
  }

  // Share cards
  async shareToChannel(type: 'item' | 'strand' | 'build' | 'daily', channelId: string, id?: string): Promise<CommandResult<{ posted: boolean }>> {
    return this.request('POST', '/api/share/post', { type, id, channelId });
  }

  // Social Presence
  async inspectPlayer(discordId: string): Promise<CommandResult<InspectData>> {
    return this.request('GET', `/api/players/${discordId}/inspect`);
  }

  async getCampaignCompletions(discordIds: string[], stepIndex: number): Promise<CommandResult<{ completedIds: string[] }>> {
    return this.request('POST', '/api/presence/campaign-completions', { discordIds, stepIndex });
  }

  // Notifications
  async getNotifications(): Promise<CommandResult<NotificationData>> {
    return this.request('GET', '/api/notifications');
  }

  async clearNotification(type: string, unitId?: string): Promise<CommandResult<unknown>> {
    return this.request('POST', '/api/notifications/clear', { type, unitId });
  }

  async dismissCoachTip(tipId: string): Promise<CommandResult<{ pendingCoachTips: string[] }>> {
    return this.request('POST', '/api/coach/dismiss', { tipId });
  }

  // Achievements
  async getAchievements(): Promise<CommandResult<import('../types').AchievementSummary>> {
    return this.request('GET', '/api/achievements');
  }

  async ackAchievements(ids: string[]): Promise<CommandResult<{ acked: number }>> {
    return this.request('POST', '/api/achievements/ack', { ids });
  }
}

export const APIManager = new APIManagerClass();
